import { useState, useRef, useEffect } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import { GlobeIcon } from '../icons/GlobeIcon'
import { ChevronDown } from 'lucide-preact'

interface Language {
  code: string
  name: string
  nativeName: string
}

const languages: Language[] = [
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
]

export function LanguageSelector() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLanguage =
    languages.find(lang => lang.code === i18n.language) ?? languages[0]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode)
    setIsOpen(false)
    localStorage.setItem('preferredLanguage', languageCode)
  }

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='flex items-center gap-0.5 px-2 sm:px-3 py-1.5 sm:py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-3xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors'
        aria-label='Select language'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
      >
        <GlobeIcon size={18} className='text-gray-600 pr-0.5' />
        <span className='hidden sm:inline'>{currentLanguage?.nativeName}</span>
        <span className='sm:hidden'>{currentLanguage?.code.toUpperCase()}</span>
        <ChevronDown
          size={16}
          className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className='absolute right-0 z-50 mt-2.5 w-120 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'
          role='listbox'
        >
          <div className='py-2.5'>
            {languages.map(language => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`
                  flex items-center justify-between w-full px-3 py-2.5 text-sm text-left hover:bg-gray-100 transition-colors
                  ${language.code === i18n.language ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}
                `}
                role='option'
                aria-selected={language.code === i18n.language}
              >
                <div className='flex flex-col'>
                  <span className='font-medium'>{language.nativeName}</span>
                  <span className='text-xs text-gray-500'>{language.name}</span>
                </div>
                {language.code === i18n.language && (
                  <svg
                    className='w-4 h-4 text-blue-600'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M5 13l4 4L19 7'></path>
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
